import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Activity, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Clock, 
  Wifi, 
  Database, 
  Mail, 
  CreditCard, 
  Server,
  Thermometer,
  Droplets,
  Zap,
  Heart,
  TrendingUp,
  Users,
  MapPin,
  Calendar,
  RefreshCw,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription } from '../components/ui/alert';
import healthService, { HealthCheckResponse, ServiceHealth } from '../services/healthService';

const STATUS_COLORS = {
  operational: 'bg-[#2ECC71] text-white', // brand green
  degraded: 'bg-[#F5542D] text-white', // brand orange
  outage: 'bg-[#F5542D] text-white', // brand orange
  maintenance: 'bg-[#0884E4] text-white', // brand blue
  unknown: 'bg-[#6B7280] text-white', // brand grey
};

const STATUS_BANNER_COLORS = {
  healthy: 'bg-[#2ECC71] text-white', // brand green
  degraded: 'bg-[#F5542D] text-white', // brand orange
  unhealthy: 'bg-[#F5542D] text-white', // brand orange
  maintenance: 'bg-[#0884E4] text-white', // brand blue
};

const STATUS_LABELS = {
  healthy: 'All Systems Operational',
  degraded: 'Some Systems Degraded',
  unhealthy: 'Major Outage',
  maintenance: 'Scheduled Maintenance',
};

function UptimeBar({ uptime }: { uptime: number }) {
  // Show 20 segments, green if uptime > 99, yellow if > 95, else red
  const segments = 20;
  const filled = Math.round((uptime / 100) * segments);
  let color = '#28a745';
  if (uptime < 99 && uptime >= 95) color = '#ffc107';
  if (uptime < 95) color = '#dc3545';
  return (
    <div className="flex items-center gap-1">
      {[...Array(segments)].map((_, i) => (
        <div
          key={i}
          className="h-2 w-2 rounded-sm"
          style={{ backgroundColor: i < filled ? color : '#e5e7eb', opacity: i < filled ? 1 : 0.3 }}
        />
      ))}
    </div>
  );
}

const StatusPage: React.FC = () => {
  const [health, setHealth] = useState<HealthCheckResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHealth = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await healthService.checkSystemHealth();
      setHealth(data);
    } catch (e: any) {
      setError('Failed to fetch system status.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const overallStatus = health?.status || 'healthy';
  const bannerColor = STATUS_BANNER_COLORS[overallStatus as keyof typeof STATUS_BANNER_COLORS] || 'bg-[#28a745] text-white';
  const bannerLabel = STATUS_LABELS[overallStatus as keyof typeof STATUS_LABELS] || 'All Systems Operational';

  return (
    <>
      <Helmet>
        <title>SaunaDip Status - System Health & Uptime</title>
        <meta name="description" content="Real-time status of SaunaDip services, uptime monitoring, and incident reports." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-white dark:bg-[#22223d] font-sans">
        {/* Top Banner */}
        <div className={`w-full py-4 px-2 text-center text-lg font-semibold shadow ${bannerColor} rounded-b-lg`}
          style={{ borderBottom: `4px solid var(--primary, #0884E4)` }}>
          {bannerLabel}
          <span className="ml-2 text-xs font-normal text-gray-200">{health?.timestamp && `Last updated: ${new Date(health.timestamp).toLocaleString()}`}</span>
        </div>

        <div className="max-w-2xl mx-auto mt-8">
          <h1 className="text-2xl font-bold mb-6 text-[#0884E4] flex items-center gap-2">
            <img src="/assets/logos/saunadip-logo.svg" alt="SaunaDip" className="h-8 w-8 inline-block" />
            SaunaDip System Status
          </h1>
          {loading && <div className="text-center text-gray-500 py-8">Loading system status…</div>}
          {error && <div className="text-center text-red-600 py-8">{error}</div>}
          {!loading && !error && health && (
            <div className="space-y-4">
              {Object.entries(health.services || {}).map(([service, svc]) => {
                const serviceName = service || 'Unknown Service';
                const serviceStatus = svc?.status || 'unknown';
                const serviceUptime = svc?.uptime || 0;
                
                return (
                  <div key={service} className="flex flex-col md:flex-row items-center justify-between bg-white dark:bg-[#2c3e50] shadow rounded-lg p-4 border-l-8 mb-2"
                    style={{ borderLeftColor: serviceStatus === 'operational' ? '#2ECC71' : serviceStatus === 'degraded' || serviceStatus === 'outage' ? '#F5542D' : serviceStatus === 'maintenance' ? '#0884E4' : serviceStatus === 'unknown' ? '#6B7280' : '#dc3545' }}>
                    <div className="flex-1 flex flex-col md:flex-row md:items-center gap-2 w-full">
                      <span className="font-medium text-lg md:w-48 text-[#2c3e50] dark:text-white">
                        {serviceName.charAt(0).toUpperCase() + serviceName.slice(1)}
                      </span>
                      <div className="flex-1 flex flex-col gap-1">
                        <UptimeBar uptime={serviceUptime} />
                        <span className="text-xs text-gray-500">{serviceUptime}% uptime</span>
                      </div>
                      <span className={`ml-auto px-3 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[serviceStatus as keyof typeof STATUS_COLORS] || 'bg-gray-500 text-white'}`}>
                        {serviceStatus.charAt(0).toUpperCase() + serviceStatus.slice(1)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default StatusPage; 